/**
 * Shared base mixin for all metrics page views (class, assignment, custom report).
 * Provides common computed properties and placeholder export/print methods.
 */
import { getMetricsFolder } from "@/api/requests/metrics.js";
import { exportElementToPdf } from "@/utils/exportToPdf.js";

export default {
  computed: {
    // Override in each view with actual loading flags
    isPageLoading() {
      return false;
    },

    // Override in each view: page type only, e.g. "metrics-overview"
    pdfFileName() {
      return "metrics-report";
    },

    // Override in each view if landscape is needed
    pdfOrientation() {
      return "portrait";
    },

    // Override in each view: subject in parens, e.g. "(Grade 10 - English)"
    pdfSubjectName() {
      return "";
    },

    entityId() {
      const { orgId, userId } = this.$route.params;
      return orgId || userId;
    },

    metricsBasePath() {
      const { orgId, userId } = this.$route.params;
      return orgId
        ? `/metrics/organization/${orgId}/user/${userId}`
        : `/metrics/user/${userId}`;
    },

    metricsOverviewPath() {
      const { orgId, userId } = this.$route.params;
      return orgId
        ? `/metrics/organization/${orgId}/overview`
        : `/metrics/user/${userId}/overview`;
    },

    // Teacher name chip: org admin → from orgUsers store via classFolder.userId; non-admin → current user
    teacherName() {
      const { orgId } = this.$route.params;
      if (orgId) {
        const userId = this.classFolder?.userId;
        if (userId) {
          const user = this.$store.getters["orgUsers/getUserById"](userId);
          if (user) {
            const name = `${user.firstName || ""} ${
              user.lastName || ""
            }`.trim();
            if (name) return name;
          }
        }
      }
      const storeUser = this.$store.state.user;
      if (!storeUser) return null;
      return (
        `${storeUser.firstName || ""} ${storeUser.lastName || ""}`.trim() ||
        null
      );
    },
  },

  methods: {
    goToStudent(studentId, classId) {
      const path = `${this.metricsBasePath}/class/${classId}/student/${studentId}`;
      if (this.isDemoMode) {
        this.$router.replace(path);
      } else {
        this.$router.push(path);
      }
    },

    getFolderFromProvider(folderId) {
      if (this.workspacesProvider.loadingFolders) return null;
      const key = this.workspacesProvider.findWorkspaceKeyByFolderId(
        Number(folderId)
      );
      return key
        ? this.workspacesProvider.getFolderById(key, Number(folderId))
        : null;
    },

    async fetchEntityFolder(folderId) {
      if (!this.entityId) return null;
      try {
        const result = await getMetricsFolder({
          entityId: this.entityId,
          folderId,
        });
        return result || null;
      } catch {
        return null;
      }
    },

    async exportToPdf() {
      const element = this.$el;
      if (!element) return;

      // Resolve teacher name for org admin views
      const { orgId, userId } = this.$route.params;
      let teacherSuffix = "";
      if (orgId && userId) {
        await this.$store.dispatch("orgUsers/load", orgId);
        const teacher =
          this.$store.getters["orgUsers/getUserByClerkId"](userId);
        if (teacher) {
          const name = `${teacher.firstName || ""} ${
            teacher.lastName || ""
          }`.trim();
          if (name) teacherSuffix = ` - ${name}`;
        }
      }

      const now = new Date();
      const date = now.toLocaleDateString("en-CA");
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const subject = this.pdfSubjectName ? ` ${this.pdfSubjectName}` : "";
      const filename = `${this.pdfFileName}${teacherSuffix}${subject} ${date} ${hh}:${mm}`;

      // scrollerView is the real scroll container (ref in App.vue)
      const scrollerView = this.$root.$refs.scrollerView;

      this.isExportingPdf = true;
      await this.$nextTick();
      // Wait for ResizeObserver → Chart.js re-render after CSS layout change
      await new Promise((resolve) => setTimeout(resolve, 200));

      try {
        await exportElementToPdf(element, filename, {
          orientation: this.pdfOrientation,
          scrollContainer: scrollerView ?? element,
          onclone: (_doc, clonedEl) => {
            // Chart.js renders at devicePixelRatio (e.g. 2x on Retina).
            // html2canvas would stretch the 2x canvas into the 1x CSS box.
            // Fix: copy pixel data into a canvas sized to the visual (CSS) dimensions.
            const origCanvases = element.querySelectorAll("canvas");
            const clonedCanvases = clonedEl.querySelectorAll("canvas");
            origCanvases.forEach((orig, i) => {
              const cloned = clonedCanvases[i];
              if (!cloned) return;
              const { width: w, height: h } = orig.getBoundingClientRect();
              if (!w || !h) return;
              cloned.width = w;
              cloned.height = h;
              cloned.style.width = `${w}px`;
              cloned.style.height = `${h}px`;
              cloned.getContext("2d").drawImage(orig, 0, 0, w, h);
            });
          },
        });
      } finally {
        this.isExportingPdf = false;
        window.dispatchEvent(new Event("resize"));
      }
    },

    print() {
      window.print();
    },
  },
};
